import classNames from 'classnames';
import orderBy from 'lodash/orderBy';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import Highlighter from 'react-highlight-words';
import { Button, ButtonGroup, Currency, Divider, Interpolate } from '@wg/wows-react-uikit';

import { highlighterPercentRegexp } from '~/settings';
import { isInGame } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  supplyAccumulativeResSelector,
  supplyBuildingInProgressSelector,
  supplyHasBuildPermissionSelector,
  supplyPersonalResSelector,
} from '~/store/selectors/supplySelectors';
import { playClanBaseBuilding, playClanBaseSelect } from '~/web2ClientAPI/sounds';
import { actionsSupply, increaseBuildingLevelThunk, setSelectedBuildingThunk } from '~/Actions/ActionSupply';

import { Building } from '~/UIKit';

import styles from './Sidebar.module.scss';

import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { TREASURY_TABS } from '~/constants';
import type { RootState } from '~/store';
import type {
  ISupplyBuilding,
  ISupplyBuildingLevel,
  ISupplyBuildingLevelTemp,
  ISupplyBuildings,
} from '~/store/slices/settingsSlice';
import type { IBattlesStats } from '~/types/declaration';

type IProps = {
  confirmBuildingId: Nullable<BUILDINGS>;
  selectedBuildingId: Nullable<BUILDINGS>;
  battlesStats: IBattlesStats;
  buildings: ISupplyBuildings;
  isOwnBase: boolean;
  isSidebarOpened: boolean;
  mapHost: string;
  tempHoverBuildingLevel: Nullable<ISupplyBuildingLevelTemp>;
  navigateToTreasury: (tab?: TREASURY_TABS) => void;
};

const SideBar: React.FC<IProps> = ({
  battlesStats,
  buildings,
  confirmBuildingId,
  isOwnBase,
  isSidebarOpened,
  mapHost,
  selectedBuildingId,
  tempHoverBuildingLevel,
  navigateToTreasury,
}) => {
  const accumulativeResource = useAppSelector(supplyAccumulativeResSelector);
  const balance = useAppSelector(supplyPersonalResSelector);
  const buildingInProgress = useAppSelector(supplyBuildingInProgressSelector);
  const hasBuildPermission = useAppSelector(supplyHasBuildPermissionSelector);
  const regularRewards = useAppSelector((state: RootState) => state.ReducerTreasury.regularRewards);

  const dispatch = useAppDispatch();

  const hideBuildingConfirm = useCallback(() => {
    dispatch(actionsSupply.hideBuildingConfirm());
  }, [dispatch]);

  const dropTempHoverLevel = useCallback(() => {
    if (tempHoverBuildingLevel) {
      dispatch(actionsSupply.dropTempHoverBuildingLevel());
    }
  }, [dispatch, tempHoverBuildingLevel]);

  const showBuildingConfirm = useCallback(
    (building: ISupplyBuilding) => {
      dispatch(actionsSupply.showBuildingConfirm(building.name));
    },
    [dispatch],
  );

  const increaseBuildingLevel = (buildingId: BUILDINGS) => () => {
    playClanBaseBuilding();
    void dispatch(increaseBuildingLevelThunk(buildingId));
  };

  const setTempHoverBuildingLevel = useCallback(
    (buildingId: BUILDINGS, level: ISupplyBuildingLevelTemp) => {
      dispatch(actionsSupply.setTempHoverBuildingLevel(buildingId, level));
    },
    [dispatch],
  );

  const setTempHoverLevel = (building: ISupplyBuilding) => (meta: ISupplyBuildingLevel['meta'], level: number) => {
    setTempHoverBuildingLevel(building.name, {
      ...meta,
      level: level,
    });
  };

  const buildingRefs = useRef<Record<string, Nullable<HTMLDivElement>>>({});

  const buildingsList = useMemo(() => {
    const buildingsList: ISupplyBuilding[] = Object.values(buildings).map((building) => {
      building.isBuildingComplete = building.level === building.maxLevel;
      return building;
    });
    return orderBy(buildingsList, ['isBuildingComplete', 'sortOrder']);
  }, [buildings]);

  useEffect(() => {
    setTimeout(() => {
      if (selectedBuildingId) {
        const scrollEl = buildingRefs.current[selectedBuildingId];
        scrollEl?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }, 300);
  }, [selectedBuildingId]);

  const sidebarClassname = classNames(styles.sidebar, {
    [styles.isOpen]: isSidebarOpened,
    [styles.sidebarOffset]: !isInGame,
  });

  return (
    <div
      className={sidebarClassname}
      onMouseEnter={() => {
        window.tooltipProvider?.hide();
      }}
    >
      <Divider type="minor" />
      <div className={styles.sidebarBuildings}>
        {buildingsList.map((building) => {
          const buildingLevel = building.levels[building.level];
          const meta = buildingLevel?.meta || null;
          if (!meta) {
            return null;
          }

          const isSelected = selectedBuildingId == building.name;
          const isConfirmVisible = confirmBuildingId == building.name && !!building.levels[building.level + 1];

          return (
            <div
              ref={(el) => {
                buildingRefs.current[building.name] = el;
              }}
              id={`sidebar-${building.name}`}
              key={`building_${building.name}`}
              className={`${styles.sidebarBuilding} ${isSelected ? styles.isSelected : ''}`}
              onClick={() => {
                if (building && isOwnBase) {
                  dwhExport.push(DWH_EVENTS.SUPPLY.VIEW_BUILDING, {
                    building_id: building.name,
                  });
                }
                playClanBaseSelect();

                dispatch(setSelectedBuildingThunk(building.name));
              }}
            >
              <Divider type="major" />
              <Building
                balance={balance}
                accumulativeResource={accumulativeResource}
                {...building}
                battlesStats={battlesStats}
                hasBuildPermission={hasBuildPermission}
                isBuildingInProgress={buildingInProgress === building.name}
                isConfirmVisible={isConfirmVisible}
                isOwnBase={isOwnBase}
                isSelected={isSelected}
                isUpgradeDisabled={!isOwnBase}
                mapHost={mapHost}
                regularRewards={regularRewards}
                onLevelClick={() => {
                  showBuildingConfirm(building);
                }}
                onMouseOverLevel={setTempHoverLevel(building)}
                onMouseOutLevel={dropTempHoverLevel}
                navigateToTreasury={navigateToTreasury}
              />
              <Divider type="minor" />
              {isConfirmVisible && (
                <div key={`Confirm_${building.name}`} className={classNames(styles.buildConfirmWrap, styles.isShow)}>
                  <div className={styles.buildConfirm}>
                    <div className={styles.buildConfirmTitle}>
                      {t('Улучшение: %(nextLevel)s', { nextLevel: building.level + 1 })}
                    </div>

                    <div className={styles.buildingConfirmBonus}>
                      {building.levels[building.level + 1]?.meta?.bonus && (
                        <>
                          <div>{t('Улучшение дает следующий бонус:')}</div>
                          <Highlighter
                            highlightClassName="HighlightPercent"
                            searchWords={highlighterPercentRegexp}
                            textToHighlight={building.levels[building.level + 1].meta.bonus}
                          />
                        </>
                      )}
                    </div>
                    <Interpolate
                      className={styles.buildingConfirmQuestion}
                      str={t('Улучшить за %(cost)s?')}
                      cost={
                        <Currency
                          isFlat
                          isBold
                          type="oil"
                          amount={building.levels[building.level + 1]?.meta?.cost || 0}
                        />
                      }
                    />
                    <ButtonGroup textAlign="left" isLoading={buildingInProgress === building.name}>
                      <Button isFlat onClick={increaseBuildingLevel(building.name)}>
                        {t('Да')}
                      </Button>
                      <Button isFlat onClick={hideBuildingConfirm}>
                        {t('Нет')}
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              )}
              <Divider type="minor" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(SideBar);
